import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import ReactApexChart from 'react-apexcharts';
import {
  getAssetMeter,
  createAssetMeter
} from '../../api/asset-meterAPI';
import { getMeters } from '../../api/metersAPI';
import {
  getMeterReadings,
  createMeterReadings,
  updateMeterReadings,
  deleteMeterReadings,
  estimateMeterReading,
} from '../../api/meter-readingsAPI';
import { getAssetById } from '../../api/assetAPI';
import { toLocalInputFormat, nowLocalIso, nowUtcIsoWithTime, formatIntDateTime, toUtcIso } from '../../utils/dateUtils';
import { validateMeterReading } from '../../utils/useValidator';
import { handleSaveMeterReading, validateReadingEntry } from '../../utils/meterReadingHelper';

const AssetMeterReadings = ({ assetId }) => {
  const [assetMeters, setAssetMeters] = useState([]);
  const [meterList, setMeterList] = useState([]);
  const [selectedMeterId, setSelectedMeterId] = useState(null);
  const [readings, setReadings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [newReading, setNewReading] = useState({ readingDate: '', value: '' });
  const [errors, setErrors] = useState({});
  const [showAddMeterModal, setShowAddMeterModal] = useState(false);
  const [newMeterId, setNewMeterId] = useState(null);
  const [newStartReading, setNewStartReading] = useState('');
  const [newUtilisation, setNewUtilisation] = useState('');
  const [newPrimaryMeter, setNewPrimaryMeter] = useState(false);

  const [assetStartDate, setAssetStartDate] = useState(null);

  const [dateRange, setDateRange] = useState('1_year');
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  const [startDate, setStartDate] = useState(oneYearAgo.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));
  const [chartOptions, setChartOptions] = useState({});
  const [chartSeries, setChartSeries] = useState([]);
  const [estimatedReading, setEstimatedReading] = useState(null);
  const [rangeStartEstimate] = useState(null);
  const MIN_AXIS_VALUE = 0;

  const getDateBoundary = (dateStr, boundary) => {
    if (!dateStr) return null;
    if (dateStr.includes('T')) {
      const parsed = new Date(dateStr);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const timePart = boundary === 'end' ? 'T23:59:59.999' : 'T00:00:00';
    const isoString = toLocalInputFormat(`${dateStr}${timePart}`);
    const parsed = new Date(isoString);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const loadMeters = async () => {
    try {
      const [am, meters] = await Promise.all([getAssetMeter(), getMeters()]);
      const assetMetersForAsset = am.filter((m) => m.assetId === assetId);
      setAssetMeters(assetMetersForAsset);
      setMeterList(meters);
      if (assetMetersForAsset.length > 0) {
        const first = [...assetMetersForAsset].sort((a, b) => a.meterId - b.meterId)[0];
        setSelectedMeterId(first.meterId);
      }
    } catch (err) {
      console.error('Error loading meters', err);
    }
  };

  const loadAsset = async () => {
    try {
      const asset = await getAssetById(assetId);
      setAssetStartDate(toUtcIso(asset.assetStartDate));
    } catch (err) {
      console.error('Error loading asset', err);
    }
  };

  useEffect(() => {
    if (!assetStartDate) return;
    setStartDate((prevStart) => {
      const normalizedPrev = toUtcIso(prevStart);
      if (!normalizedPrev) return assetStartDate;
      return normalizedPrev < assetStartDate ? assetStartDate : normalizedPrev;
    });
  }, [assetStartDate]);

  const loadReadings = async (meterId = selectedMeterId) => {
    if (!assetId || !meterId) return;
    try {
      const data = await getMeterReadings();
      const filtered = data
        .filter((r) => r.assetId === assetId && r.metersId === meterId)
        .sort((a, b) => new Date(b.readingDate) - new Date(a.readingDate));
      setReadings(filtered);
    } catch (err) {
      console.error('Failed to load readings', err);
    }
  };

  useEffect(() => {
    if (assetId) {
      loadMeters();
      loadAsset();
    }
  }, [assetId]);

  useEffect(() => {
    loadReadings();
  }, [selectedMeterId, assetId]);

  useEffect(() => {
    const start = getDateBoundary(startDate, 'start');
    const end = getDateBoundary(endDate, 'end');
    const within = readings
      .filter((r) => {
        const d = new Date(r.readingDate);
        return (!start || d >= start) && (!end || d <= end);
      })
      .sort((a, b) => new Date(a.readingDate) - new Date(b.readingDate));

    let dataPoints = within
      .map((r) => {
        const parsedDate = new Date(r.readingDate);
        const value = Number(r.value);
        if (Number.isNaN(parsedDate.getTime()) || !Number.isFinite(value)) {
          return null;
        }
        return {
          x: parsedDate.toISOString(),
          y: value,
        };
      })
      .filter(Boolean);

    const startEstimatePoint = (() => {
      if (!rangeStartEstimate) return null;
      const parsed = new Date(rangeStartEstimate.readingDate);
      const estimatedValue = Number(rangeStartEstimate.value);
      if (Number.isNaN(parsed.getTime()) || !Number.isFinite(estimatedValue)) {
        return null;
      }
      return { x: parsed.toISOString(), y: estimatedValue, isRangeStart: true };
    })();

    if (startEstimatePoint) {
      const startTime = new Date(startEstimatePoint.x).getTime();
      const alreadyHasStartPoint = dataPoints.some(
        (point) => new Date(point.x).getTime() === startTime,
      );
      if (!alreadyHasStartPoint) {
        dataPoints = [startEstimatePoint, ...dataPoints];
      }
    }

    const lastPoint = dataPoints.length > 0 ? dataPoints[dataPoints.length - 1] : null;
    const shouldAttemptEstimateLine = Boolean(estimatedReading && lastPoint);

    const chartPoints = [...dataPoints];
    if (chartPoints.length === 0 && readings.length === 0) {
      const fallbackDate = getDateBoundary(startDate, 'start') || new Date();
      if (fallbackDate && !Number.isNaN(fallbackDate.getTime())) {
        chartPoints.push({ x: fallbackDate.toISOString(), y: 0, isRangeStart: Boolean(startDate) });
      }
    }
    let hasEstimatePoint = false;
    if (shouldAttemptEstimateLine) {
      const parsedEstimateDate = new Date(estimatedReading.readingDate);
      const estimatedValue = Number(estimatedReading.value);
      if (!Number.isNaN(parsedEstimateDate.getTime()) && Number.isFinite(estimatedValue)) {
        chartPoints.push({
          x: parsedEstimateDate.toISOString(),
          y: parseInt(estimatedValue, 10),
          isEstimate: true,
        });
        hasEstimatePoint = true;
      }
    }

    if (!hasEstimatePoint && lastPoint) {
      const lastPointDate = new Date(lastPoint.x);
      if (lastPointDate < new Date()) {
        chartPoints.push({ x: nowLocalIso(), y: lastPoint.y });
      }
    }

    const discreteMarkers = hasEstimatePoint
      ? [
        {
          seriesIndex: 0,
          dataPointIndex: chartPoints.length - 1,
          fillColor: '#fff',
          strokeColor: '#0d6efd',
          size: 6,
        },
      ]
      : [];

    const yAxisValues = chartPoints
      .map((point) => {
        const numericValue = Number(point?.y);
        return Number.isFinite(numericValue) ? numericValue : null;
      })
      .filter((val) => val !== null);

    let yAxisMin;
    let yAxisMax;
    if (yAxisValues.length > 0) {
      yAxisMin = Math.min(...yAxisValues);
      yAxisMax = Math.max(...yAxisValues);
      if (yAxisMin === yAxisMax) {
        const padding = Math.abs(yAxisMin || 1) * 0.05 || 1;
        yAxisMin -= padding;
        yAxisMax += padding;
      }
    }

    if (yAxisMin === undefined || yAxisMin === null) {
      yAxisMin = MIN_AXIS_VALUE;
    } else if (yAxisMin < MIN_AXIS_VALUE) {
      yAxisMin = MIN_AXIS_VALUE;
    }

    if (yAxisMax === undefined || yAxisMax === null) {
      yAxisMax = Math.max(yAxisMin + 1, 1);
    } else if (yAxisMax < yAxisMin) {
      yAxisMax = Math.max(yAxisMin + 1, yAxisMin + Math.abs(yAxisMax - yAxisMin));
    }

    setChartSeries([{ name: 'Reading', data: chartPoints }]);
    const forecastOptions = hasEstimatePoint
      ? { count: 1, dashArray: 8, strokeWidth: 3, fillOpacity: 0 }
      : { count: 0 };
    setChartOptions({
      legend: { show: false },
      chart: { type: 'line', toolbar: { show: false } },
      noData: {
        text: 'No meter readings available',
        align: 'center',
        style: { color: '#6B7280', fontSize: '13px' },
      },
      dataLabels: { enabled: false },
      stroke: {
        curve: 'straight',
        width: 3,
        colors: ['#0d6efd'],
        dashArray: 0,
      },
      forecastDataPoints: forecastOptions,
      grid: {
        padding: {
          left: 30,
          right: 10,
        },
        show: true,
        borderColor: '#D1D5DB',
        strokeDashArray: 1,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      markers: {
        colors: ['#45B369'],
        strokeWidth: 2,
        size: 0,
        hover: { size: 6 },
        discrete: discreteMarkers,
      },
      xaxis: { type: 'datetime', labels: { style: { fontSize: '12px' } } },
      yaxis: {
        tickAmount: 6,
        labels: {
          style: { fontSize: '12px' },
          padding: 50,
          formatter: (val) => {
            const rounded = Math.round(val / 10) * 10;
            return rounded.toLocaleString();
          },
        },
        min: yAxisMin,
        max: yAxisMax,
      }

    });
  }, [readings, startDate, endDate, estimatedReading, assetStartDate, rangeStartEstimate]);

  // Get an estimated reading if there is not a recent reading
  useEffect(() => {
    let cancelled = false;
    const maybeEstimateReading = async () => {
      if (!assetId || !selectedMeterId || readings.length === 0) {
        if (!cancelled) {
          setEstimatedReading(null);
        }
        return;
      }

      const now = new Date();

      try {
        const estimate = await estimateMeterReading({
          assetId,
          taskId: -1,
          meterId: selectedMeterId,
          providedDate: now.toISOString(),
        });
        const estimatedValue = Number(
          estimate && estimate.estimatedValue !== undefined
            ? estimate.estimatedValue
            : estimate?.value,
        );
        if (!cancelled) {
          if (Number.isFinite(estimatedValue)) {
            setEstimatedReading({ readingDate: nowUtcIsoWithTime(), value: estimatedValue });
          } else {
            setEstimatedReading(null);
          }
        }
      } catch (err) {
        console.error('Error estimating meter reading', err);
        if (!cancelled) {
          setEstimatedReading(null);
        }
      }
    };

    maybeEstimateReading();

    return () => {
      cancelled = true;
    };
  }, [assetId, selectedMeterId, readings]);

  const handleSelectMeter = (e) => {
    setSelectedMeterId(Number(e.target.value));
  };

  const handleRangeChange = (e) => {
    const val = e.target.value;
    setDateRange(val);
    const end = new Date();
    const start = new Date();
    let nextStartDate = null;

    const { parsedAssetStart, assetStartString } = (() => {
      if (!assetStartDate) {
        return { parsedAssetStart: null, assetStartString: null };
      }
      const parsed = new Date(assetStartDate);
      if (!Number.isNaN(parsed.getTime())) {
        return {
          parsedAssetStart: parsed,
          assetStartString: parsed.toISOString().slice(0, 10),
        };
      }
      if (typeof assetStartDate === 'string') {
        const truncated = assetStartDate.slice(0, 10);
        if (truncated) {
          const fallback = new Date(`${truncated}T00:00:00`);
          return {
            parsedAssetStart: Number.isNaN(fallback.getTime()) ? null : fallback,
            assetStartString: truncated,
          };
        }
      }
      return { parsedAssetStart: null, assetStartString: null };
    })();

    switch (val) {
      case '1_month':
        start.setMonth(start.getMonth() - 1);
        break;
      case '3_months':
        start.setMonth(start.getMonth() - 3);
        break;
      case '6_months':
        start.setMonth(start.getMonth() - 6);
        break;
      case '1_year':
        start.setFullYear(start.getFullYear() - 1);
        break;
      case '2_years':
        start.setFullYear(start.getFullYear() - 2);
        break;
      case '3_years':
        start.setFullYear(start.getFullYear() - 3);
        break;
      case 'all_time':
        if (assetStartDate) {
          const parsed = new Date(assetStartDate);
          if (!Number.isNaN(parsed.getTime())) {
            nextStartDate = parsed.toISOString().slice(0, 10);
          } else if (typeof assetStartDate === 'string') {
            nextStartDate = assetStartDate.slice(0, 10);
          }
        }
        if (!nextStartDate) {
          start.setTime(0);
        }
        break;
      default:
        return;
    }

    if (!nextStartDate && parsedAssetStart) {
      if (start < parsedAssetStart) {
        nextStartDate = assetStartString ?? parsedAssetStart.toISOString().slice(0, 10);
      }
    }
    const resolvedStartDate =
      nextStartDate ?? start.toISOString().slice(0, 10);
    setStartDate(resolvedStartDate);
    setEndDate(end.toISOString().slice(0, 10));
  };

  const getMeterName = (meterId) => {
    const m = meterList.find((mt) => mt.id === meterId);
    return m ? m.name : `Meter ${meterId}`;
  };

  const getMeterUnitName = (meterId) => {
    const m = meterList.find((mt) => mt.id === meterId);
    return m?.measurementUnits?.unitName || '';
  };

  const handleOpenModal = (reading = null) => {
    setEditingReading(reading);
    setErrors({});
    setNewReading({
      readingDate: toLocalInputFormat(reading?.readingDate) || nowLocalIso(),
      value: reading?.value ?? '',
    });
    setShowModal(true);
  };

  const validateReading = async () => validateReadingEntry({
    newReading,
    assetId,
    meterId: selectedMeterId,
    assetMeters,
    setErrors,
    validateMeterReadingApi: validateMeterReading,
    meterErrorKey: 'value',
  });

  const handleSaveReading = async () => handleSaveMeterReading({
    newReading,
    assetId,
    meterId: selectedMeterId,
    assetMeters,
    setErrors,
    validateMeterReadingApi: validateMeterReading,
    createReading: createMeterReadings,
    updateReading: updateMeterReadings,
    editingId: editingReading?.id,
    validateReadingFn: validateReading,
    onSuccess: async () => {
      setShowModal(false);
      await loadReadings(selectedMeterId);
    },
    meterErrorKey: 'value',
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reading?')) return;
    try {
      await deleteMeterReadings(id);
      await loadReadings(selectedMeterId);
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleSaveMeter = async () => {
    if (!newMeterId) return;
    try {
      await createAssetMeter({
        assetId,
        meterId: newMeterId,
        startReading: newStartReading || 0,
        utilisation: newUtilisation || 0,
        primaryMeter: newPrimaryMeter ? 1 : 0,
      });
      if (assetStartDate) {
        await createMeterReadings({
          assetId,
          metersId: newMeterId,
          value: parseFloat(newStartReading) || 0,
          readingDate: new Date(assetStartDate).toISOString(),
        });
      }
      setShowAddMeterModal(false);
      setNewMeterId(null);
      setNewStartReading('');
      setNewUtilisation('');
      setNewPrimaryMeter(false);
      await loadMeters();
      setSelectedMeterId(newMeterId);
    } catch (err) {
      console.error('Error adding meter', err);
    }
  };

  const filteredStart = getDateBoundary(startDate, 'start');
  const filteredEnd = getDateBoundary(endDate, 'end');
  const filtered = readings.filter((r) => {
    const match =
      r.readingDate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(r.value).toLowerCase().includes(searchTerm.toLowerCase());
    const d = new Date(r.readingDate);
    return (
      match &&
      (!filteredStart || d >= filteredStart) &&
      (!filteredEnd || d <= filteredEnd)
    );
  });

  const estimatedTableRow =
    estimatedReading && readings.length > 0
      ? {
        id: 'estimated-reading',
        readingDate: estimatedReading.readingDate,
        value: parseInt(estimatedReading.value, 10),
        metersId: selectedMeterId ?? readings[0]?.metersId,
        isEstimate: true,
      }
      : null;
  const tableRows = estimatedTableRow ? [estimatedTableRow, ...filtered] : filtered;

  return (
    <div className="row g-3">
      {assetMeters.length === 0 ? (
        <div className="text-center py-40">
          <p>No meters assigned to this asset</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => {
              setNewPrimaryMeter(assetMeters.length === 0);
              setShowAddMeterModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add Meter
          </button>
        </div>
      ) : (
        <>
          <div className="col-6 d-flex align-items-center">
            <select
              className="form-select form-select-sm w-auto"
              value={selectedMeterId || ''}
              onChange={handleSelectMeter}
            >
              {assetMeters.map((am) => (
                <option key={am.id} value={am.meterId}>
                  {getMeterName(am.meterId)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 d-flex justify-content-end align-items-center flex-wrap gap-2">
            <select
              className="form-select form-select-sm w-auto"
              value={dateRange}
              onChange={handleRangeChange}
            >
              <option value="1_month">1 Month</option>
              <option value="3_months">3 Months</option>
              <option value="6_months">6 Months</option>
              <option value="1_year">1 Year</option>
              <option value="2_years">2 Years</option>
              <option value="3_years">3 Years</option>
              <option value="all_time">All Time</option>
            </select>
            <input
              type="text"
              className="form-control form-control-sm me-2 w-auto"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-primary btn-sm" onClick={() => handleOpenModal()}>
              <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
              Add Meter Reading
            </button>
          </div>
          <div className="table-responsive overflow-auto scroll-sm" style={{ maxHeight: '300px' }}>
            <table className="table table-sm bordered-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Value</th>
                  <th>Unit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className={r.isEstimate ? 'text-primary fw-semibold' : ''}>
                        {formatIntDateTime(r.readingDate)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className={r.isEstimate ? 'text-primary fw-semibold' : ''}>{parseInt(r.value, 10)}</span>
                        {r.isEstimate && (
                          <span className="badge bg-primaryFocus text-primary text-xxs">EST</span>
                        )}
                      </div>
                    </td>
                    <td>{getMeterUnitName(r.metersId)}</td>
                    <td>
                      {r.isEstimate ? (
                        <span></span>
                      ) : (
                        <div className="d-flex">
                          <button
                            className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-4"
                            onClick={() => handleOpenModal(r)}
                          >
                            <Icon icon="ic:baseline-edit" />
                          </button>
                          <button
                            className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleDelete(r.id)}
                          >
                            <Icon icon="ic:baseline-delete-outline" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="w-100">
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="line"
              height={250}
              width="100%"
            />
          </div>
        </>
      )}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingReading ? 'Edit Meter Reading' : 'Add Meter Reading'}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Date / Time *</label>
                  <input
                    type="datetime-local"
                    className={`form-control ${errors.readingDate ? 'is-invalid' : ''}`}
                    value={newReading.readingDate}
                    min={toLocalInputFormat(assetStartDate)}
                    max={nowLocalIso()}
                    onChange={(e) => setNewReading({ ...newReading, readingDate: e.target.value })}
                  />
                  {errors.readingDate && <div className="invalid-feedback">{errors.readingDate}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Value *</label>
                  <input
                    type="number"
                    step="any"
                    className={`form-control ${errors.value ? 'is-invalid' : ''}`}
                    value={newReading.value}
                    onChange={(e) => setNewReading({ ...newReading, value: e.target.value })}
                  />
                  {errors.value && <div className="invalid-feedback">{errors.value}</div>}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveReading}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddMeterModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Meter</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowAddMeterModal(false);
                    setNewMeterId(null);
                    setNewStartReading('');
                    setNewUtilisation('');
                    setNewPrimaryMeter(false);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Meter *</label>
                  <select
                    className="form-select"
                    value={newMeterId || ''}
                    onChange={(e) => setNewMeterId(Number(e.target.value))}
                  >
                    <option value="" disabled>Select Meter</option>
                    {meterList.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Start Reading</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newStartReading}
                    onChange={(e) => setNewStartReading(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Utilisation</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newUtilisation}
                    onChange={(e) => setNewUtilisation(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Primary Meter</label>
                  <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={newPrimaryMeter}
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddMeterModal(false);
                    setNewMeterId(null);
                    setNewStartReading('');
                    setNewUtilisation('');
                    setNewPrimaryMeter(false);
                  }}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveMeter}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetMeterReadings;
